/**
 * Login.jsx - DB-backed sign-in form.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import { useAuth } from "../auth/apiAuth.jsx";
import { useToast } from "./../components/Toast.jsx";

export default function LoginPage() {
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hovered, setHovered] = useState(false);

  const fromPath = location.state?.from?.pathname || "/dashboard";

  // Press Enter to submit
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" && !submitting) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password, submitting]);

  const handleSubmit = useCallback(async () => {
    setError("");
    if (!username.trim() || !password) {
      setError("Please enter both username and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login({ username: username.trim(), password });
      toast("Signed in successfully", "good");
      navigate(fromPath, { replace: true });
    } catch (err) {
      const msg = err?.message || "Sign-in failed. Please try again.";
      setError(msg);
      toast(msg, "bad");
    } finally {
      setSubmitting(false);
    }
  }, [username, password, login, navigate, fromPath, toast]);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoWrap}>
          <img
            src="/ysu-logo.png"
            alt="YSU Food Solutions"
            style={s.logoImg}
          />
        </div>

        <h1 style={s.title}>Vendor Management System</h1>
        <p style={s.subtitle}>Purchasing, sourcing &amp; supplier performance</p>

        <div style={s.divider} />

        <form style={s.form} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <label style={s.label}>
            <span style={s.labelText}>Username</span>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. admin"
              disabled={submitting}
              style={s.input}
            />
          </label>

          <label style={s.label}>
            <span style={s.labelText}>Password</span>
            <div style={s.pwWrap}>
              <input
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={submitting}
                style={{ ...s.input, paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                style={s.pwToggle}
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error && <div style={s.error}>{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              ...s.submit,
              ...(hovered && !submitting ? s.submitHover : {}),
            }}
          >
            {submitting ? (
              <Loader2 size={18} className="spin" />
            ) : (
              <LogIn size={18} />
            )}
            <span style={s.submitText}>
              {submitting ? "Signing in…" : "Sign in"}
            </span>
          </button>
        </form>

        <p style={s.footerNote}>
          Sign in with your authorized credentials to continue.
        </p>
      </div>

      <div style={s.attribution}>
        Vendor Management System
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const s = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg)",
    padding: 24,
    gap: 24,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-lg)",
    padding: "40px 36px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "center",
  },
  logoWrap: { marginBottom: 20, display: "flex", justifyContent: "center" },
  logoImg: {
    width: 180,
    height: "auto",
    objectFit: "contain",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 750,
    color: "var(--ink)",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: "8px 0 0",
    fontSize: 13.5,
    fontWeight: 450,
    color: "var(--ink-3)",
    lineHeight: 1.5,
  },
  divider: {
    width: "100%",
    height: 1,
    background: "var(--line-2)",
    margin: "24px 0",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    textAlign: "left",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  labelText: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-2)",
    letterSpacing: "0.01em",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    fontSize: 14,
    color: "var(--ink)",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  pwWrap: { position: "relative" },
  pwToggle: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "var(--ink-3)",
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
  },
  error: {
    fontSize: 13,
    color: "var(--bad, #c0392b)",
    background: "var(--bad-bg, rgba(192, 57, 43, 0.08))",
    border: "1px solid var(--bad, #c0392b)",
    borderRadius: "var(--radius-sm)",
    padding: "8px 12px",
    textAlign: "left",
  },
  submit: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--accent-ink, #fff)",
    background: "var(--accent)",
    border: "1px solid var(--accent)",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
    marginTop: 6,
  },
  submitHover: {
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.12)",
    filter: "brightness(1.05)",
  },
  submitText: { letterSpacing: "-0.01em" },
  footerNote: {
    margin: "16px 0 0",
    fontSize: 12,
    color: "var(--ink-3)",
  },
  attribution: {
    fontSize: 11.5,
    color: "var(--ink-3)",
    fontWeight: 450,
    letterSpacing: "0.01em",
  },
};
